<template>
  <div :class="[this.shouldShowCustomLogo ? '' : $style.short, $style.formContainer]">
    <LoginCard
      :header="headerTitle"
      :showCustomLogo="shouldShowCustomLogo"
    >
      <p :class="$style.subheader">
        {{ mfaDisplayText.subheaderText }}
      </p>

      <slot />

      <Alert v-model="errorMessage" />

      <LoginInputConfirmation
        v-if="!shouldShowTotpInput"
        @input="token = $event"
        :isLoading="isLoading"
        @onSubmit="submit"
        @onRequestReset="requestResetTOTP"
        label="Enter code from your mobile authenticator app:"
        placeholder="Enter Code"
        :token="token"
        :buttonText="mfaDisplayText.buttonText"
        :shouldShowTOTPResetLink="shouldShowTOTPResetLink"
      />

      <TotpInput
        v-if="shouldShowTotpInput"
        label="Enter code from your mobile authenticator app:"
        :isLoading="isLoading"
        @interface="getChildInterface"
        @submit="submit"
        v-model="token"
      />

      <ChooseMfa
        v-if="shouldShowMfaChooser"
        v-bind="$props"
        :selectedFactor="factor"
      />
    </LoginCard>
    <!-- This component is also in ChooseMfa, so only show it here if necessary -->
    <LogoIcon v-if="!shouldShowMfaChooser" />
  </div>
</template>

<script>
// Frameworks
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

// Constants and Types
import { LoginConsts } from 'client/util/Constants';
import { Totp } from 'client/containers/Login/MfaType';
import MfaStrings from 'client/containers/Login/MfaStrings';

// Router
import RouterGenerator, { AdminRoutes, UserRoutes } from 'client/routes/RouteGenerator';

// API
import AdminLoginApi from 'client/api/AdminLoginApi';
import api from 'client/api';

// Utils
import Util from 'client/util/Util';
import loginResponseHandlers from 'client/containers/Login/LoginResponseHandlers';
import loginUtil from 'client/components/mixins/loginUtil';

// Components
import ChooseMfa from 'client/containers/Login/ChooseMfa.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import LoginCard from 'client/components/LoginCard.vue';
import LoginInputConfirmation from 'client/components/forms/LoginInputConfirmation.vue';
import TotpInput from 'client/components/TotpInput.vue';

const { user, admin } = LoginConsts.clientType;
const { login } = RouterGenerator;

const { responseMessages } = LoginConsts;
const {
  invalidOtpError,
  passwordResetSuccess,
  totpResetError,
} = responseMessages;
const { resetTOTP } = AdminRoutes;

export default {
  name: 'VerifyTOTP',

  LocalStorageService,

  components: {
    ChooseMfa,
    LoginCard,
    LoginInputConfirmation,
    TotpInput,
  },

  childInterface: {
    clearInput: () => {},
  },

  props: {
    factor: {
      type: Totp,
      default: () => new Totp(),
    },
    factors: {
      type: Array,
      default: () => ([]),
    },
    context: {
      type: String,
      default: '',
    },
    mfaDisplayText: {
      type: Object,
      default: () => MfaStrings.contexts.totp.login,
    },
    redirectTo: {
      type: String,
      default: '',
    },
    passwordResetInfo: {
      type: Object,
      default: null,
    },
    shouldShowCustomLogo: {
      type: Boolean,
      default: true,
    },
    headerTitle: {
      type: String,
      default: 'Verify Your Identity',
    },
  },

  mixins: [
    loginResponseHandlers,
    loginUtil,
  ],

  data: () => ({
    token: '',
  }),

  computed: {
    shouldShowMfaChooser() {
      return this.clientType !== admin;
    },
    shouldShowTOTPResetLink() {
      return this.clientType === admin
        && this.passwordResetInfo === null;
    },
    ...mapGetters('LoginOauthAdminFlowModel', [
      'loginChallenge',
    ]),
    shouldShowTotpInput() {
      return this.$hasFeatureFlag(this.$featureFlags.identityFirst);
    },
  },

  methods: {
    ...mapActions('ResetPasswordModel', [
      'commitPropertyChange',
      'verifyResetPassword',
    ]),

    getChildInterface(childInterface) {
      this.$options.childInterface = childInterface;
    },

    clearInput() {
      this.$options.childInterface.clearInput();
    },

    /**
     * Computes the destination upon a successful MFA verification. The two variables it computes
     * the destination against are if a redirectTo prop is present or if the user has MFA configured
     * @param  {Boolean} mfaConfigured [data from the user object]
     * @return {String}                [route that the user should be redirected to]
     */
    computeSuccessDestination(mfaConfigured) {
      const {
        dashboard, oauthRedirectUrl, ssoRedirectUrl,
      } = UserRoutes;
      const truthString = arg => (arg ? 't' : 'f');
      const redirectCheck = `${truthString(this.context)}${truthString(mfaConfigured)}`;

      let destination;

      if (redirectCheck === 'tt' && this.context === LoginConsts.sso) {
        destination = ssoRedirectUrl(this.redirectTo);
      } else if (redirectCheck === 'tt' && this.context === LoginConsts.oauth) {
        destination = oauthRedirectUrl(this.redirectTo);
      } else {
        /*
        this will get overwritten by the redirect() method
        in loginUtil if redirectTo is set in session storage
        */
        destination = dashboard;
      }

      return destination;
    },

    async completeAdminLoginFlow(resp) {
      if (this.loginChallenge) {
        try {
          const { data: { redirect_to: oidcRedirect } } = await AdminLoginApi
            .completeLoginChallengeOIDC(this.loginChallenge);
          Util.redirect(oidcRedirect);
        } catch (_) {
          // TODO AUTH-6980 - Configures OIDC error page
        }
      } else {
        const lastUrl = get(resp, 'data.lastUrl', AdminRoutes.dashboard);

        this.redirect(lastUrl || AdminRoutes.dashboard);
      }
    },

    /**
     * This is not an efficient way of checking the user data. In the future, the loginMFA endpoint
     * should return the user object so we don't have to make another request here.
     */
    handleSuccess(resp) {
      if (this.clientType === user) {
        // following line can only be UserLoginApi
        return api[user].getUser()
          .then((res) => {
            const mfaConfigured = get(res, 'data.mfa.configured', false);
            this.redirect(this.computeSuccessDestination(mfaConfigured));
          }).catch(() => {
            this.handleError('There was an error. Please try again later.');
          });
      }

      return this.completeAdminLoginFlow(resp);
    },

    handleError(message = invalidOtpError) {
      this.setErrorMessage(message);
      this.token = '';
      this.clearInput();
    },

    handleAdminPasswordResetError(error) {
      this.errorMessage = get(error, 'response.data.message', 'Verification code was invalid or already used.');
      const status = get(error, 'response.status');
      // 403 indicates invalid TOTP code exceeds max attempts
      if (status === 403) {
        this.vueRouteChange('Admin Login', { error: this.errorMessage });
      }
    },

    handlePasswordResetError(error) {
      this.token = '';
      // See LoginResponseHandlers
      this.mfaErrorRedirectHandler(this.factor.name, error, this.passwordResetInfo)();
    },

    resetPasswordRequest() {
      if (this.clientType === admin) {
        return this.commitPropertyChange({
          key: 'otp',
          value: this.token,
        }).then(() => this.verifyResetPassword())
          .then(() => {
            this.$options.LocalStorageService.setItem('resetSuccessMessage', passwordResetSuccess);
            this.redirect(AdminRoutes.logout);
          })
          .catch(this.handleAdminPasswordResetError);
      }
      return api[this.clientType].resetPassword(
        this.passwordResetInfo.confirmationCode,
        {
          ...this.passwordResetInfo,
          otp: this.token,
        },
      )
        .then((res) => {
          const { email, totpKey } = res.data;
          const redirectUrl = totpKey ? UserRoutes.setupMfa(totpKey, email) : login;
          this.redirect(redirectUrl);
        })
        .catch(this.handlePasswordResetError);
    },

    requestResetTOTP() {
      api.admin.requestTOTPReset()
        .then(() => this.redirect(resetTOTP))
        .catch(() => this.handleError(totpResetError));
    },

    verifyTotp() {
      return api[this.clientType]
        .verifyTotp({ otp: this.token })
        .then(this.handleSuccess)
        .catch(this.handleTOTPError);
    },

    handleTOTPError(error) {
      const route = this.clientType === admin ? 'Admin Login' : 'Login';
      const status = get(error, 'response.status');
      const message = get(error, 'response.data.message', '');

      // 403 for too many failed attempts
      if (status === 403) {
        this.vueRouteChange(route, { error: message });
      } else {
        this.handleError(invalidOtpError);
      }
    },
    /**
     * Submits the MFA token for verification. It is called by the submit function
     * from the loginUtil mixin.
     */
    request() {
      let apiRequest = this.verifyTotp;
      if (this.passwordResetInfo && this.passwordResetInfo.confirmationCode) {
        apiRequest = this.resetPasswordRequest;
      }

      return apiRequest().finally(() => {
        this.isLoading = false;
      });
    },
  },
};
</script>

<style module>
@import '../css/login-components.css';

@value (jcGrey800) from 'css/brand-colors.css';

.short {
  padding-top: 0;
}

.formContainer fieldset {
  margin-top: 1.5rem;
}

.formContainer button {
  margin-top: 0.5rem;
}

.subheader {
  font-size: 0.75rem;
  font-weight: 600;
}
</style>
